import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Users, Music, PlayCircle, Gift } from "lucide-react";

/**
 * HomeStatsStrip — a thin social-proof band directly below the hero.
 * Makes brand-new visitors (usually landing from a FB/X share) see the
 * site is populated in one glance: "12 artists · 34 tracks · 2.4K plays".
 *
 * On Mondays with active free drops, an extra ember chip appears at the
 * end and doubles as advertising for Melo Mondays.
 *
 * Fails silently and hides itself if the endpoint's unreachable.
 */
function compact(n) {
  if (!n || n < 1) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

export default function HomeStatsStrip() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats/public").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  if (!s) return null;
  const stats = [
    { icon: Users, label: "Artists", value: compact(s.artists) },
    { icon: Music, label: "Tracks", value: compact(s.tracks) },
    { icon: Sparkles, label: "Albums", value: compact(s.albums) },
    { icon: PlayCircle, label: "Plays", value: compact(s.total_plays) },
  ];
  return (
    <div data-testid="home-stats-strip" className="border-y border-[#2A2A2A] bg-black/40">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        {stats.map((st) => {
          const Icon = st.icon;
          return (
            <div key={st.label} data-testid={`stat-${st.label.toLowerCase()}`}
                 className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-[#FF4500] shrink-0" />
              <span className="font-display text-xl tabular-nums">{st.value}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{st.label}</span>
            </div>
          );
        })}
        {s.melo_monday_active && s.monday_free > 0 && (
          <a
            href="/radio"
            data-testid="stat-monday-free"
            className="flex items-center gap-2 border border-[#FF4500] bg-[#FF4500]/10 px-3 py-1.5 hover:bg-[#FF4500]/20 transition-colors ml-auto"
          >
            <Gift className="h-4 w-4 text-[#FF4500] animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#FF4500] font-bold">
              {s.monday_free} free drop{s.monday_free === 1 ? "" : "s"} today
            </span>
          </a>
        )}
      </div>
    </div>
  );
}
